import { useState, useEffect, useCallback } from "react";
import { apiClient } from "../../services/api";
import RefreshButton from "../RefreshButton/RefreshButton";

function HuginnPrompt() {
  const estimateTokens = (text) => Math.ceil((text || "").length / 4);

  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [updatedAt, setUpdatedAt] = useState("");
  const [activeVersion, setActiveVersion] = useState(0);
  const [versions, setVersions] = useState([]);

  const fetchPrompt = useCallback(async () => {
    try {
      const data = await apiClient.get("/huginn-prompt");
      setPrompt(data.prompt || "");
      setUpdatedAt(data.updatedAt || "");
      setActiveVersion(data.activeVersion || 0);
      setVersions(data.versions || []);
    } catch (err) {
      console.error("Failed to fetch Huginn prompt:", err);
    }
  }, []);

  useEffect(() => {
    fetchPrompt();
  }, [fetchPrompt]);

  const handleSave = async () => {
    setLoading(true);
    setSaved(false);
    try {
      const data = await apiClient.put("/huginn-prompt", { prompt });
      setUpdatedAt(data.updatedAt || "");
      setActiveVersion(data.activeVersion || data.version || 0);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      const refreshed = await apiClient.get("/huginn-prompt");
      setVersions(refreshed.versions || []);
    } catch (err) {
      console.error("Failed to save Huginn prompt:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleActivateVersion = async (version) => {
    if (!window.confirm(`Restore Huginn prompt version ${version}?`)) return;
    setLoading(true);
    try {
      await apiClient.put("/huginn-prompt", { activateVersion: version });
      await fetchPrompt();
    } catch (err) {
      console.error("Failed to activate version:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="annacore-page">
      <div className="annacore-header">
        <div>
          <h2>🌐 Huginn — Web Search Agent</h2>
          <p className="annacore-subtitle">
            Manage the system prompt for Huginn, Anna's sandboxed web search sub-agent (GPT-4.1-mini)
          </p>
        </div>
        <RefreshButton onClick={fetchPrompt} title="Refresh Huginn prompt" />
      </div>

      <div className="core-prompt-section">
        <p className="prompt-description">
          Huginn receives this prompt as its system instruction when Anna dispatches a web_search action.
          Changes take effect immediately on the next search.
          {activeVersion > 0 && <span className="prompt-version-badge">v{activeVersion}</span>}
        </p>
        <div className="prompt-token-bar">
          <span>{prompt.length.toLocaleString()} chars</span>
          <span className="prompt-token-sep">·</span>
          <span>~{estimateTokens(prompt).toLocaleString()} tokens</span>
        </div>
        <textarea
          className="prompt-editor"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="Enter the system prompt for Huginn..."
          spellCheck={false}
        />
        <div className="prompt-actions">
          <button
            className="btn-primary"
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? "Saving..." : "Save as New Version"}
          </button>
          {saved && <span className="save-confirm">✓ Saved as v{activeVersion}</span>}
          {updatedAt && (
            <span className="prompt-updated">
              Last updated: {new Date(updatedAt).toLocaleString()}
            </span>
          )}
        </div>

        {versions.length > 0 && (
          <div className="prompt-versions">
            <h3>Version History</h3>
            <div className="versions-list">
              {versions.map((v) => (
                <div
                  key={v.version}
                  className={`version-item ${v.version === activeVersion ? "active" : ""}`}
                >
                  <div className="version-info">
                    <span className="version-number">v{v.version}</span>
                    {v.version === activeVersion && <span className="version-active-tag">Active</span>}
                    <span className="version-date">
                      {v.createdAt ? new Date(v.createdAt).toLocaleString() : "—"}
                    </span>
                    <span className="version-chars">
                      {v.charCount?.toLocaleString()} chars · ~{Math.ceil((v.charCount || 0) / 4).toLocaleString()} tokens
                    </span>
                  </div>
                  <div className="version-preview">{v.preview}</div>
                  {v.version !== activeVersion && (
                    <button
                      className="btn-restore"
                      onClick={() => handleActivateVersion(v.version)}
                      disabled={loading}
                    >
                      Restore
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default HuginnPrompt;
